// Copyright (c) 2025 Greener Power Solutions.
// This file is part of Greener Power Solutions' software,
// and may not be altered, copied, redistributed, or sold
// without specific permission.
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useFlag } from '@unleash/proxy-client-react';
import ProjectSidebarHeader from 'components/layout/ProjectSidebarHeader';
import { Icons } from 'greener-ui-library';
import useAuth from 'hooks/useAuth';
import useProject from 'hooks/useProject';
import { useSidebarItems } from 'hooks/useSidebarItems';
import { GreenerUnit } from 'types';
import { canReadAsset, canSeeReports } from 'utils/authorization';
import { getSortedByActivityUnits, isBatteryAsset } from 'utils/units';

function constructProjectMenu({ t, projectSlug }) {
  const menuItems = [
    {
      name: t('projects:navigation.home'),
      path: `/projects/${projectSlug}`,
    },
    {
      name: t('projects:navigation.overview'),
      path: `/projects/${projectSlug}/administration`,
    },
    {
      name: t('projects:navigation.configurationDiagram'),
      path: `/projects/${projectSlug}/setups`,
    },
  ];

  return {
    isVisible: true,
    name: t('projects:navigation.project'),
    icon: () => <Icons.Project color="secondary" />,
    items: menuItems,
  };
}

function constructMonitoringMenu({ t, projectSlug, unitId, theme, isHmiEnabled, isBatterySelected }) {
  if (!unitId) {
    return null;
  }

  const menuItems = [
    {
      name: t('projects:navigation.unitControl'),
      path: `/projects/${projectSlug}/monitoring/${unitId}/asset`,
    },
    {
      name: t('projects:navigation.graphs'),
      path: `/projects/${projectSlug}/monitoring/${unitId}/graphs`,
    },
    {
      name: t('projects:navigation.multiBatteryGraphs'),
      path: `/projects/${projectSlug}/monitoring/multi-battery-graphs?units=${unitId}`,
    },
    ...(isHmiEnabled && isBatterySelected
      ? [
          {
            name: t('projects:navigation.hmi'),
            path: `/projects/${projectSlug}/monitoring/${unitId}/hmi/overview`,
          },
        ]
      : []),
  ];

  return {
    isVisible: true,
    name: t('projects:navigation.unitMonitoring'),
    icon: () => <Icons.Graphs strokeColor={theme.palette.secondary.main} />,
    items: menuItems,
  };
}

function constructAnalyticsMenu({ t, projectSlug, userRole, theme }) {
  const menuItems = [
    {
      isVisible: canSeeReports(userRole),
      name: t('projects:navigation.reports'),
      path: `/projects/${projectSlug}/analytics/reports`,
    },
    {
      isVisible: canReadAsset(userRole),
      name: t('projects:navigation.analyticsSummary'),
      path: `/projects/${projectSlug}/analytics/summary`,
    },
    {
      isVisible: canReadAsset(userRole),
      name: t('projects:navigation.assetAnalytics'),
      path: `/projects/${projectSlug}/analytics/assets`,
    },
  ];

  const visibleItems = menuItems.filter((item) => item.isVisible);

  return {
    isVisible: visibleItems.length,
    name: t('projects:navigation.analytics'),
    icon: () => <Icons.Analytics strokeColor={theme.palette.secondary.main} />,
    items: visibleItems,
  };
}

function determinePreselectedUnitExtId(unitMonitoringMatch, units) {
  const matchedId = unitMonitoringMatch?.params.unitExternalIdentifier;
  const isActualUnit = units?.some((u) => u.greenerUnit?.externalIdentifier === matchedId);
  if (matchedId && isActualUnit) {
    return matchedId;
  }

  if (units && units.length > 0) {
    const sortedUnits = getSortedByActivityUnits(units);
    return sortedUnits[0]?.externalIdentifier;
  }

  return null;
}

export function SidebarItems() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { projectSlug } = useParams();
  const unitMonitoringMatch = useMatch('/projects/:projectSlug/monitoring/:unitExternalIdentifier/*');
  const { dispatchSidebar } = useSidebarItems();
  const { project, projectUnitInstances } = useProject();
  const theme = useTheme();
  const isHmiEnabled = useFlag('hmi-gui');

  const unitExternalIdentifier = determinePreselectedUnitExtId(unitMonitoringMatch, projectUnitInstances);
  const selectedAsset = projectUnitInstances?.find(
    (instance) => instance.greenerUnit?.externalIdentifier === unitExternalIdentifier
  )?.greenerUnit;
  const isBatterySelected = isBatteryAsset(selectedAsset as GreenerUnit);

  useEffect(() => {
    const projectMenu = constructProjectMenu({ t, projectSlug });
    const monitoringMenu = constructMonitoringMenu({
      t,
      projectSlug,
      unitId: unitExternalIdentifier,
      theme,
      isHmiEnabled,
      isBatterySelected,
    });
    const analyticsMenu = constructAnalyticsMenu({ t, projectSlug, userRole: user?.role, theme });
    const items = [projectMenu, monitoringMenu, analyticsMenu].filter((i) => i !== null && i.isVisible);

    dispatchSidebar?.({
      type: 'REGULAR',
      payload: {
        sidebarHeader: <ProjectSidebarHeader project={project} />,
        items,
      },
    });
  }, [project, user, projectSlug, dispatchSidebar, t, unitExternalIdentifier, isHmiEnabled, isBatterySelected]);
}

SidebarItems.displayName = '[Project].sidebar.tsx';
